// 개발요청 — 한 칸의 «첨부 줄»(2판 V2): 📎 고르기 · 그 칸에 Ctrl+V(캡처) · 칸 상자에 끌어다 놓기 · 칩(그림은 썸네일) · 보내기 전 검사.
// 🔴 이 부품은 올리지 않는다 — 고르거나 붙인 파일은 부모(새 요청·고치기 창)가 저장할 때 field 를 붙여 올린다.
// 🔴 검사는 서버 services/devreq_files.py 의 거울이다(ALLOWED_EXT · MAX_BYTES 100MB · MAX_FILES 10 — 칸 구분 없이 합산).
//    상한은 부모가 내려 주는 otherCount(이 칸 밖에서 세는 첨부 수)에 이 칸의 기존 칩·고른 파일을 더해 센다.
// 상세(DevRequestDetail)·기타 첨부(DevRequestFiles)·추가 요청(DevRequestAsks)도 여기의 상수·썸네일 훅·내려받기·
//   읽기용 첨부 묶음(FieldFiles)을 같이 쓴다.
import { Fragment, useEffect, useRef, useState } from 'react';
import { devreqAPI } from '../../api/client';
import { COLORS, RADIUS, btn } from '../../utils/uiStyles';

export const MAX_FILE_MB = 100;
export const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;   // = devreq_files.MAX_BYTES
export const MAX_FILES = 10;                                // = devreq_files.MAX_FILES
export const ALLOWED_EXT = ['pdf', 'doc', 'docx', 'hwp', 'hwpx', 'xls', 'xlsx', 'ppt', 'pptx', 'png', 'jpg', 'jpeg', 'txt', 'csv', 'zip', '7z', 'rar', 'alz', 'egg', 'tar', 'gz', 'tgz'];
// 종류별 묶음(화면 안내 «올릴 수 있는 파일» 전용) — 마스터 원문 순서. 합집합 = ALLOWED_EXT(시험이 대조한다).
// IMAGE_EXT(썸네일 판정)는 따로 둔다 — «브라우저가 바로 그리는 형식»이라 뜻이 다르다.
export const ALLOWED_EXT_GROUPS = [
  { label: '문서', exts: ['pdf', 'doc', 'docx', 'hwp', 'hwpx', 'xls', 'xlsx', 'ppt', 'pptx'] },
  { label: '그림', exts: ['jpg', 'jpeg', 'png'] },
  { label: '압축', exts: ['zip', '7z', 'rar', 'alz', 'egg', 'tar', 'gz', 'tgz'] },
  { label: '텍스트', exts: ['txt', 'csv'] },
];
export const ACCEPT =ALLOWED_EXT.map((ext) => `.${ext}`).join(',');
export const TOO_BIG = '파일이 100MB를 넘습니다.';         // = devreq_files.TOO_BIG
export const TOO_MANY = '요청 1건에 첨부는 10개까지입니다.';  // = routers/devreq.py upload_request_file 의 409
const BAD_TYPE = '허용되지 않는 파일 형식입니다(pdf·doc·docx·hwp·hwpx·xls·xlsx·ppt·pptx·png·jpg·txt·csv·zip·7z·rar·alz·egg·tar·gz·tgz).';   // = devreq_files.BAD_EXT
const PICK_LABEL = '📎 파일 첨부';
export const ATTACH_GUIDE = '캡처는 이 칸에 Ctrl+V 로 붙여 넣거나, 파일을 여기로 끌어다 놓으세요. (그림·문서·압축 · 파일당 100MB · 요청당 10개)';
// 첨부 칸 — 서버 devreq_rules.FILE_FIELDS 중 ① 글 칸 셋(ask_answer 는 추가 요청 화면 몫).
export const FIELD_FILE_NAMES = ['current_way', 'wanted_feature', 'reference_note'];

const IMAGE_EXT = ['png', 'jpg', 'jpeg'];
// 캡처로 받는 그림 형식 → 붙일 확장자.
const PASTE_IMAGE_EXT = { 'image/png': 'png', 'image/jpeg': 'jpg' };
const THUMB_MAX_BYTES = 2 * 1024 * 1024;   // 이보다 큰 그림은 자동 썸네일 없이 이름 칩(누르면 그때 받는다)

export const extOf = (name, ext) => {
  if (ext) return String(ext).toLowerCase();
  const s = String(name || '');
  const at = s.lastIndexOf('.');
  return at >= 0 ? s.slice(at + 1).toLowerCase() : '';
};
export const isImageFile = (file) => IMAGE_EXT.includes(extOf(file?.name, file?.ext));
const canThumb = (file) => isImageFile(file) && Number(file?.size) <= THUMB_MAX_BYTES;

// jsdom·옛 브라우저엔 createObjectURL 이 없을 수 있다 — 없으면 썸네일 없이 이름 칩으로 보인다.
export function makeUrl(blob) {
  try {
    return blob && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(blob) : null;
  } catch {
    return null;
  }
}
export function dropUrl(url) {
  try {
    if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
  } catch { /* 이미 해제됨 */ }
}

// 새 창에 열지 않고 blob 을 그 이름으로 저장한다(링크로 열면 토큰이 안 실린다). 실패는 호출한 쪽이 문구로 바꾼다.
export async function saveDevreqFile(file) {
  const r = await devreqAPI.download(file.id);
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name || 'attachment';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// 끌어다 놓은 것이 파일인가(글을 끌어 놓는 것은 브라우저 기본 동작대로 둔다).
export function dropHasFiles(event) {
  const dt = event?.dataTransfer;
  if (!dt) return false;
  if (dt.files && dt.files.length > 0) return true;
  return Array.from(dt.types || []).includes('Files');
}

// 이미 올라간 그림 첨부의 썸네일 — 2MB 이하만 · 한 장씩 차례로 받는다(인증 헤더가 필요해 <img src="/api/…"> 를 쓰지 않는다).
//   id → object URL 을 ref 에 캐시한다: 목록을 다시 받아도 받은 것은 다시 내려받지 않고, 목록에서 빠진 id 만 revoke,
//   이 훅을 쓰는 화면이 사라질 때 전부 revoke.
export function useFileThumbs(files) {
  const cacheRef = useRef(new Map());
  const failedRef = useRef(new Set());
  const wantRef = useRef([]);
  const idsRef = useRef(new Set());
  const runningRef = useRef(false);
  const aliveRef = useRef(true);
  const [urls, setUrls] = useState({});

  useEffect(() => {
    aliveRef.current = true;
    const cache = cacheRef.current;
    return () => {
      aliveRef.current = false;
      cache.forEach((url) => dropUrl(url));
      cache.clear();
    };
  }, []);

  const publish = () => {
    if (aliveRef.current) setUrls(Object.fromEntries(cacheRef.current));
  };

  const pump = async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      while (aliveRef.current) {
        const next = wantRef.current.find((id) => !cacheRef.current.has(id) && !failedRef.current.has(id));
        if (next === undefined) break;
        let url = null;
        try {
          const r = await devreqAPI.download(next);
          if (!aliveRef.current) break;
          url = makeUrl(r?.data);
        } catch {
          url = null;
        }
        if (!url) {
          failedRef.current.add(next);   // 못 받은 그림은 이름 칩으로 둔다(다시 조르지 않는다)
        } else if (!idsRef.current.has(next)) {
          dropUrl(url);                  // 받는 사이 목록에서 빠졌다
        } else {
          cacheRef.current.set(next, url);
          publish();
        }
      }
    } finally {
      runningRef.current = false;
    }
  };

  const list = Array.isArray(files) ? files : [];
  const key = list.map((f) => `${f.id}:${canThumb(f) ? 1 : 0}`).join(',');
  useEffect(() => {
    idsRef.current = new Set(list.map((f) => f.id));
    wantRef.current = list.filter(canThumb).map((f) => f.id);
    let dropped = false;
    cacheRef.current.forEach((url, id) => {
      if (!idsRef.current.has(id)) {
        dropUrl(url);
        cacheRef.current.delete(id);
        dropped = true;
      }
    });
    if (dropped) publish();
    pump();
  }, [key]);

  return urls;
}

const pad = (n) => String(n).padStart(2, '0');
const stampOf = (d) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;

const THUMB = { width: '64px', height: '64px', objectFit: 'cover', borderRadius: RADIUS.sm, display: 'block' };
const chipStyle = {
  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 6px', maxWidth: '100%', boxSizing: 'border-box',
  border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md, background: COLORS.surface, fontSize: '12px', color: COLORS.text,
};
const extBadge = {
  padding: '1px 5px', borderRadius: RADIUS.sm, background: '#f3f4f6', color: COLORS.textSub, fontSize: '10px', fontWeight: 700,
};
const xBtn = {
  border: 'none', background: 'none', padding: '0 2px', cursor: 'pointer', color: COLORS.textSub, fontSize: '13px', lineHeight: 1,
};

// 새로 고른 그림 칩 — object URL 은 칩이 빠지거나 부품이 사라질 때 revoke.
function StagedThumb({ file }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const made = makeUrl(file);
    setUrl(made);
    return () => dropUrl(made);
  }, [file]);
  if (!url) return <span title={file.name} style={{ ...THUMB, ...extBadge, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{extOf(file.name).toUpperCase()}</span>;
  return <img src={url} alt={file.name} title={file.name} style={THUMB} />;
}

function DocLabel({ name, ext }) {
  return (
    <>
      <span style={extBadge}>{(extOf(name, ext) || '?').toUpperCase()}</span>
      <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>{name}</span>
    </>
  );
}

const fileChip = {
  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 8px', maxWidth: '100%', boxSizing: 'border-box', cursor: 'pointer',
  border: `1px solid ${COLORS.border}`, borderRadius: '8px', background: COLORS.surface, color: COLORS.primaryDark,
  fontSize: '12px', fontFamily: 'inherit', textAlign: 'left', overflowWrap: 'anywhere',
};
const extTag = { padding: '1px 5px', borderRadius: '6px', background: '#f3f4f6', color: COLORS.textSub, fontSize: '10px', fontWeight: 700 };

// 읽기용 첨부 묶음(상세의 칸 첨부 · 추가 요청 기록) — 그림은 썸네일 격자(높이 120px · 넘치면 줄바꿈 · 누르면 크게 보기)
//   · 썸네일이 없는 그림(2MB 초과 등)과 문서는 이름 칩. onRemove 를 주면 can_delete 인 것에만 «<이름> 지우기».
export function FieldFiles({ label, files, thumbs, onOpen, onDownload, onRemove }) {
  if (!files.length) return null;
  const removeButton = (file) => (onRemove && file.can_delete === true ? (
    <button type="button" aria-label={`${file.name} 지우기`} onClick={() => onRemove(file)} style={xBtn}>✕</button>
  ) : null);
  return (
    <div role="group" aria-label={`${label} 첨부`}
      style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px', whiteSpace: 'normal' }}>
      {files.map((file) => {
        const ext = (extOf(file.name, file.ext) || '?').toUpperCase();
        let chip;
        if (isImageFile(file) && thumbs[file.id]) {
          chip = (
            <button type="button" aria-label={`${file.name} 크게 보기`} onClick={(e) => onOpen(file, e.currentTarget)}
              style={{ padding: 0, border: `1px solid ${COLORS.border}`, borderRadius: '8px', background: COLORS.surface, cursor: 'zoom-in', maxWidth: '100%' }}>
              <img src={thumbs[file.id]} alt={file.name} style={{ display: 'block', height: '120px', maxWidth: '100%', objectFit: 'contain', borderRadius: '8px' }} />
            </button>
          );
        } else if (isImageFile(file)) {
          chip = (
            <button type="button" aria-label={`${file.name} 크게 보기`} onClick={(e) => onOpen(file, e.currentTarget)} style={fileChip}>
              <span style={extTag}>{ext}</span><span>{file.name}</span>
            </button>
          );
        } else {
          chip = (
            <button type="button" aria-label={`${file.name} 내려받기`} onClick={() => onDownload(file)} style={fileChip}>
              <span style={extTag}>{ext}</span><span>{file.name}</span>
            </button>
          );
        }
        const remove = removeButton(file);
        if (!remove) return <Fragment key={file.id}>{chip}</Fragment>;
        return (
          <span key={file.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', maxWidth: '100%' }}>{chip}{remove}</span>
        );
      })}
    </div>
  );
}

export default function DevRequestAttachField({
  label, files = [], staged = [], onStage, onUnstage, onRemoveExisting, otherCount = 0, textareaRef, disabled = false,
  thumbs = {}, style, children,
}) {
  const [error, setError] = useState('');
  const [over, setOver] = useState(false);
  const dragDepthRef = useRef(0);
  const inputRef = useRef(null);
  const pasteSeqRef = useRef({ stamp: '', n: 0 });

  // 보내기 전 검사 — 형식·100MB·상한(이 칸 밖 + 이 칸 기존 칩 + 고른 것). 상한에 걸리면 앞에서부터 들어가는 만큼만.
  const accept = (list) => {
    if (disabled || list.length === 0) return;
    const problems = [];
    const ok = [];
    let room = MAX_FILES - (otherCount + files.length + staged.length);
    let full = false;
    list.forEach((file) => {
      const name = file.name || '이름 없는 파일';
      if (!ALLOWED_EXT.includes(extOf(file.name))) problems.push(`${name}: ${BAD_TYPE}`);
      else if (file.size > MAX_FILE_BYTES) problems.push(`${name}: ${TOO_BIG}`);
      else if (room <= 0) full = true;
      else {
        ok.push(file);
        room -= 1;
      }
    });
    if (full) problems.push(TOO_MANY);
    setError(problems.join(' '));
    if (ok.length > 0) onStage?.(ok);
  };
  // 붙여넣기 리스너는 한 번만 건다 — 늘 마지막으로 그린 때의 검사(그때의 상한·disabled)를 부르게 ref 로 넘긴다.
  const acceptRef = useRef(accept);
  acceptRef.current = accept;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  // 캡처는 이름이 없거나 image.png 다 → «캡처_YYYYMMDD_HHMMSS.png»(같은 초 여러 장이면 _2·_3). 그 밖 이름은 그대로.
  const nameCapture = (file) => {
    const ext = PASTE_IMAGE_EXT[file.type];
    if (!ext || (file.name && file.name !== 'image.png')) return file;
    const stamp = stampOf(new Date());
    const seq = pasteSeqRef.current;
    if (seq.stamp === stamp) seq.n += 1;
    else {
      seq.stamp = stamp;
      seq.n = 1;
    }
    const suffix = seq.n > 1 ? `_${seq.n}` : '';
    return new File([file], `캡처_${stamp}${suffix}.${ext}`, { type: file.type });
  };
  const nameCaptureRef = useRef(nameCapture);
  nameCaptureRef.current = nameCapture;

  useEffect(() => {
    const el = textareaRef?.current;
    if (!el) return undefined;
    const onPaste = (event) => {
      if (disabledRef.current) return;
      const data = event.clipboardData;
      if (!data) return;
      // 글이 함께 오면 글 붙여넣기 그대로(엑셀·워드 복사는 글과 그림이 같이 온다).
      const plain = typeof data.getData === 'function' ? data.getData('text/plain') : '';
      if (plain) return;
      const list = Array.from(data.files || []);
      if (list.length === 0) return;
      event.preventDefault();
      acceptRef.current(list.map((f) => nameCaptureRef.current(f)));
    };
    el.addEventListener('paste', onPaste);
    return () => el.removeEventListener('paste', onPaste);
  }, [textareaRef]);

  const pick = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';   // 같은 파일을 다시 고를 수 있게(복사 뒤에 비운다)
    accept(picked);
  };

  // 끌어다 놓기 — 자식 위를 지날 때 깜빡이지 않게 들어오고 나간 횟수로 강조를 켜고 끈다.
  const dragEnter = (event) => {
    event.preventDefault();
    if (disabled) return;
    dragDepthRef.current += 1;
    setOver(true);
  };
  const dragOver = (event) => {
    event.preventDefault();
    if (!disabled && !over) setOver(true);
  };
  const dragLeave = () => {
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setOver(false);
  };
  const drop = (event) => {
    dragDepthRef.current = 0;
    setOver(false);
    if (!dropHasFiles(event)) return;   // 글 끌어 놓기는 칸에 그대로 들어간다
    event.preventDefault();
    accept(Array.from(event.dataTransfer.files || []));
  };

  const hasChips = files.length > 0 || staged.length > 0;
  return (
    <div
      data-testid="devreq-attach-field"
      onDragEnter={dragEnter}
      onDragOver={dragOver}
      onDragLeave={dragLeave}
      onDrop={drop}
      style={{
        ...style,
        borderRadius: RADIUS.md,
        outline: over ? `2px dashed ${COLORS.primary}` : 'none',
        outlineOffset: '4px',
        background: over ? COLORS.primarySoft : undefined,
      }}
    >
      {children}
      <div style={{ marginTop: '6px' }}>
        <button type="button" aria-label={`${label} 파일 첨부`} onClick={() => inputRef.current?.click()} disabled={disabled}
          style={{ ...btn('secondary', 'sm'), opacity: disabled ? 0.5 : 1 }}>{PICK_LABEL}</button>
        <input type="file" multiple accept={ACCEPT} aria-label={`${label} 올릴 파일`} hidden ref={inputRef} onChange={pick} disabled={disabled} />
        <p style={{ margin: '4px 0 0', fontSize: '12px', color: COLORS.textSub }}>{ATTACH_GUIDE}</p>
        {hasChips && (
          <ul aria-label={`${label} 첨부 목록`} style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {files.map((file) => (
              <li key={`file-${file.id}`} style={chipStyle}>
                {isImageFile(file) && thumbs[file.id]
                  ? <img src={thumbs[file.id]} alt={file.name} title={file.name} style={THUMB} />
                  : <DocLabel name={file.name} ext={file.ext} />}
                {file.can_delete === true && onRemoveExisting && (
                  <button type="button" aria-label={`${file.name} 빼기`} onClick={() => onRemoveExisting(file.id)} disabled={disabled} style={xBtn}>✕</button>
                )}
              </li>
            ))}
            {staged.map((file, i) => (
              <li key={`new-${i}-${file.name}`} style={chipStyle}>
                {isImageFile(file) ? <StagedThumb file={file} /> : <DocLabel name={file.name} />}
                <button type="button" aria-label={`${file.name} 빼기`} onClick={() => onUnstage?.(i)} disabled={disabled} style={xBtn}>✕</button>
              </li>
            ))}
          </ul>
        )}
        {error && <div role="alert" style={{ marginTop: '4px', fontSize: '12px', color: COLORS.dangerText, overflowWrap: 'anywhere' }}>{error}</div>}
      </div>
    </div>
  );
}
